import React, { useEffect, useState } from 'react';
import { IconBank, IconOpenBanking } from '@americanexpress/dls-react';

import useAsyncCallback from 'src/hooks/useAsyncCallback';
import { AccountDrawerType, AccountModel } from 'src/types/account.model';
import AccountService from 'src/services/account.service';
import { InvestmentHoldingModel } from 'src/types/investment-holding.model';
import { ManualAccountType } from 'src/types/manualAccount.model';
import ManualAccountService from '../../../../../services/manualAccount.service';

import ListAccountsViewTemplate from '../listAccountsViewTemplate/ListAccountsViewTemplate';
import Callout from '../../../atoms/callout/Callout';
import WhyShouldYouConnectTooltip from '../../../atoms/tooltips/whyYouShouldConnectTooltip/WhyShouldYouConnectTooltip';
import LiteralsService from 'src/services/literals/LiteralsService';
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import { DLSSize } from 'src/types/dls-size.enum';
import { AddAccountListTemplate } from 'src/types/view-models/listAccounts/listAccountsViewModel.view.model';
import InvestmentsAndRetirementListAccountsViewModel from 'src/types/view-models/listAccounts/investmentsAndRetirement.view.model';
import Selectors from 'src/utils/selectors';

const addAccountListTemplate: AddAccountListTemplate = [
    {
        title: LiteralsService.getLiteral('Onboarding.investmentsAndRetirement.addRetirementTitle'),
        subtitle: LiteralsService.getLiteral('Onboarding.investmentsAndRetirement.addRetirementSubtitle'),
        manualAccountType: ManualAccountType['401kAnd403b']
    },
    {
        title: LiteralsService.getLiteral('Onboarding.investmentsAndRetirement.addIraTitle'),
        subtitle: LiteralsService.getLiteral('Onboarding.investmentsAndRetirement.addIraSubtitle'),
        manualAccountType: ManualAccountType.Ira
    },
    {
        title: LiteralsService.getLiteral('Onboarding.investmentsAndRetirement.addMutualFundsAndPublicStocksTitle'),
        subtitle: LiteralsService.getLiteral(
            'Onboarding.investmentsAndRetirement.addMutualFundsAndPublicStocksSubtitle'
        ),
        manualAccountType: ManualAccountType.MutualFundsAndPublicStocks
    },
    {
        title: LiteralsService.getLiteral('Onboarding.investmentsAndRetirement.addEducationTitle'),
        subtitle: LiteralsService.getLiteral('Onboarding.investmentsAndRetirement.addEducationSubtitle'),
        manualAccountType: ManualAccountType.Education
    },
    {
        title: LiteralsService.getLiteral('Onboarding.investmentsAndRetirement.addCryptoTitle'),
        subtitle: LiteralsService.getLiteral('Onboarding.investmentsAndRetirement.addCryptoSubtitle'),
        manualAccountType: ManualAccountType.Cryptocurrency
    },
    {
        title: LiteralsService.getLiteral('Onboarding.investmentsAndRetirement.addPensionTitle'),
        subtitle: LiteralsService.getLiteral('Onboarding.investmentsAndRetirement.addPensionSubtitle'),
        manualAccountType: ManualAccountType.Pension
    },
    {
        title: LiteralsService.getLiteral('Onboarding.investmentsAndRetirement.otherTitle'),
        manualAccountType: ManualAccountType.OtherInvestmentsAndRetirement
    }
];

const callout = (
    <Callout
        icon={<IconBank size={DLSSize.MEDIUM} />}
        heading={LiteralsService.getLiteral('Onboarding.cashAndBanking.calloutHeading')}
    >
        <Typography variant={TypographyVariants.BODY}>
            {LiteralsService.getLiteral('Onboarding.cashAndBanking.calloutBody')}
        </Typography>
        <WhyShouldYouConnectTooltip withText />
    </Callout>
);

const formCallout = (
    <Callout
        icon={<IconOpenBanking size={DLSSize.MEDIUM} />}
        heading={LiteralsService.getLiteral('Onboarding.investmentsAndRetirement.formCalloutHeading')}
        body={LiteralsService.getLiteral('Onboarding.investmentsAndRetirement.formCalloutBody')}
    />
);

export default function InvestmentsAndRetirementListAccountsView({
    user,
    back,
    next,
    handleReloadUser
}: InvestmentsAndRetirementListAccountsViewModel): JSX.Element {
    const [visibleAccounts, setVisibleAccounts] = useState<AccountModel[]>([]);
    const [hiddenAccounts, setHiddenAccounts] = useState<AccountModel[]>([]);
    const [nonDrawerTypeAccounts, setNonDrawerTypeAccounts] = useState<AccountModel[]>();
    const [investmentHoldings, setInvestmentHoldings] = useState<InvestmentHoldingModel[]>([]);
    const [loading, setLoading] = useState(true);

    const refreshAccounts = useAsyncCallback(async (): Promise<void> => {
        const [newVisibleAccounts, newHiddenAccounts] =
            await AccountService.getInvestmentsAndRetirementConnectedAccounts(user);
        const nonDrawerTypeAccounts = await AccountService.getNonInvestmentsAndRetirementConnectedAccounts(user);
        const { newInvestmentHoldings } = await ManualAccountService.getAccounts();
        setVisibleAccounts(newVisibleAccounts);
        setHiddenAccounts(newHiddenAccounts);
        setNonDrawerTypeAccounts(nonDrawerTypeAccounts);
        setInvestmentHoldings(
            newInvestmentHoldings.filter((e) =>
                ManualAccountService.isInvestmentsAndRetirment({ investmentHolding: e })
            )
        );
        setLoading(false);
    }, []);

    useEffect(() => {
        void refreshAccounts();
    }, [user.accounts.length]);

    if (loading) return <></>;

    return (
        <ListAccountsViewTemplate
            progressIndex={4}
            accountDrawerType={AccountDrawerType.InvestmentsAndRetirements}
            title={LiteralsService.getLiteral('Onboarding.investmentsAndRetirement.title')}
            listAccountsHeading={LiteralsService.getLiteral('Onboarding.investmentsAndRetirement.listAccountsHeading')}
            ManualAccountType={ManualAccountService.investmentsAndRetirementAccountTypes}
            visibleAccounts={visibleAccounts}
            hiddenAccounts={hiddenAccounts}
            nonDrawerTypeAccounts={nonDrawerTypeAccounts}
            investmentHoldings={investmentHoldings}
            debts={[]}
            addAccountListTemplate={addAccountListTemplate}
            callout={callout}
            formCallout={formCallout}
            refreshAccounts={refreshAccounts}
            back={back}
            next={next}
            handleReloadUser={handleReloadUser}
            data-cy={Selectors.PlanAhead.INVESTMENTS_AND_RETIREMENT}
        />
    );
}
